import { useMemo } from 'react';

import {
	AnyAri,
	Ati,
	ValidationError as AtiValidationError,
	PlatformIntegrationAri,
} from '@atlassian/ari';
import { isSearchResultGraphEntity } from '@atlassian/search-client';

import { type ProductKeys3P } from '../../../common/constants/3p-product-configs';
import {
	CONFLUENCE_ANSWER_ENTITY_ATI,
	CONFLUENCE_CALENDAR_ENTITY_ATI,
	CONFLUENCE_GLIFFY_ENTITY_ATI,
	CONFLUENCE_QUESTION_ENTITY_ATI,
	CONFLUENCE_SPACE_CALENDAR_ENTITY_ATI,
} from '../../../common/constants/entities';
import { is3pProductKeyForAnalytics, ProductKeys } from '../../../common/constants/products';
import type { ThirdPartyConfigsBootstrap } from '../../../common/constants/schemas/3p-config';
import type { IAnalyticsSearchPageResults } from '../../../common/types';
import { getProductFromProviderId } from '../../../common/utils/search-result-utils';
import { isResultSmartLink } from '../../../common/utils/third-party-utils';
import { useSelectedFilters } from '../../filters/selected';
import { useThirdPartyConfigs } from '../../store/bootstrap';

/**
 * Retrieves a product from ATI, but also supports legacy ATIs that don't follow the standard format.
 */
const parseProductFromAti = (ati: string): string | null => {
	const legacyAtiToProductMap: Record<string, ProductKeys> = {
		[CONFLUENCE_CALENDAR_ENTITY_ATI]: ProductKeys.Confluence,
		[CONFLUENCE_SPACE_CALENDAR_ENTITY_ATI]: ProductKeys.Confluence,
		[CONFLUENCE_ANSWER_ENTITY_ATI]: ProductKeys.Confluence,
		[CONFLUENCE_QUESTION_ENTITY_ATI]: ProductKeys.Confluence,
		[CONFLUENCE_GLIFFY_ENTITY_ATI]: ProductKeys.Confluence,
	};

	try {
		return Ati.parse(ati).resourceOwner;
	} catch (e) {
		if (e instanceof AtiValidationError) {
			return legacyAtiToProductMap[ati] || null;
		}
		throw e;
	}
};

/**
 * @propResults - Optional results passed in as a prop. If not provided, results will be taken from the store (RSS).
 *
 * @returns
 * productsFirstResultPositions: Map - of product to the index (actual place in the list not zero-indexed i.e. first item in result set has value 1) -  of the first result for every product in the result set
 * productsInResult: List - of the products that are present in the result set,
 * is3PInTop10Results: Boolean - If there is at least one 3P result in the first ten results
 * authorized3PProducts: List - of 3P products against which the end user has done the authorization
 * notAuthorized3PProducts: List - of 3P products against which the end user hasn't done the authorization
 * productsSearchedFor: List - of Products which were searched for. Only the products which have been authorized by the user will be searched against.
 * wasAny3PSearched: Boolean - If any 3P product was searched
 * is3PResultPresentInResultSet: Boolean - If at least one 3P result is present in the result set from any 3P product
 */
export const useSearchAndResultAnalyticsAttributes: (
	relayResults?: IAnalyticsSearchPageResults,
) => {
	searchAndResult: {
		productsFirstResultPositions: {
			[key: string]: number;
		};
		productsInResults: string[];
		is3PInTop10Results: boolean;
		is3PResultPresentInResultSet: boolean;
		is3PSmartLinkInTop10Results: boolean;
		is3PSmartLinkPresentInResultSet: boolean;
		smartLinkProductsFirstResultPositions: {
			[key: string]: number;
		};
		authorized3PProducts: string[];
		notAuthorized3PProducts: string[];
		productsSearchedFor: string[];
		wasAny3PSearched: boolean;
	};
} = (relayResults?: IAnalyticsSearchPageResults) => {
	const [thirdPartyConfigs] = useThirdPartyConfigs();

	const { selectedFilters, entities } = useSelectedFilters({});

	const productsFromEntitiesSearchedFor = entities
		.map((entity) => parseProductFromAti(entity))
		.filter((productName): productName is string => !!productName);

	const thirdPartyProductsSearchedFor =
		selectedFilters.thirdPartyFilters.thirdPartyProducts?.map((filter) => filter.product) || [];

	const productsSearchedFor = [
		...new Set([...productsFromEntitiesSearchedFor, ...thirdPartyProductsSearchedFor]),
	].filter((product) => product !== 'graph');

	const wasAny3PSearched =
		(selectedFilters.thirdPartyFilters.integrations?.length || 0) > 0 ||
		productsFromEntitiesSearchedFor.includes('graph');

	let productKeys: string[] = [];
	let authorized3PProducts: string[] = [];
	let notAuthorized3PProducts: string[] = [];
	if (thirdPartyConfigs) {
		productKeys = Object.keys(thirdPartyConfigs);

		authorized3PProducts = productKeys.filter(
			(product) => !thirdPartyConfigs[product as ProductKeys3P]?.userNeedsOAuth,
		);
		notAuthorized3PProducts = productKeys.filter(
			(product) => thirdPartyConfigs[product as ProductKeys3P]?.userNeedsOAuth,
		);
	}

	const {
		productsFirstResultPositions,
		smartLinkProductsFirstResultPositions,
		is3PInTop10Results,
		is3PResultPresentInResultSet,
		is3PSmartLinkInTop10Results,
		is3PSmartLinkPresentInResultSet,
	} = useResultSetAnalytics(relayResults, thirdPartyConfigs);

	const productsInResults = Object.keys(productsFirstResultPositions);

	return {
		searchAndResult: {
			productsFirstResultPositions,
			productsInResults,
			is3PInTop10Results,
			is3PResultPresentInResultSet,
			is3PSmartLinkInTop10Results,
			is3PSmartLinkPresentInResultSet,
			smartLinkProductsFirstResultPositions,
			authorized3PProducts,
			notAuthorized3PProducts,
			productsSearchedFor,
			wasAny3PSearched,
		},
	};
};

export function useResultSetAnalytics(
	results: IAnalyticsSearchPageResults | undefined,
	thirdPartyConfigs: ThirdPartyConfigsBootstrap,
): {
	productsFirstResultPositions: {
		[key: string]: number;
	};
	smartLinkProductsFirstResultPositions: {
		[key: string]: number;
	};
	is3PInTop10Results: boolean;
	is3PResultPresentInResultSet: boolean;
	is3PSmartLinkInTop10Results: boolean;
	is3PSmartLinkPresentInResultSet: boolean;
} {
	return useMemo(() => {
		let is3PInTop10Results = false;
		let is3PSmartLinkInTop10Results = false;
		let is3PSmartLinkPresentInResultSet = false;
		let is3PResultPresentInResultSet = false;
		const { productsFirstResultPositions, smartLinkProductsFirstResultPositions } =
			results?.edges?.reduce<{
				productsFirstResultPositions: { [key: string]: number };
				smartLinkProductsFirstResultPositions: { [key: string]: number };
			}>(
				(acc, result, index) => {
					const product = AnyAri.parse(result.node.id).resourceOwner;

					// this is always true for graph nodes
					if (acc.productsFirstResultPositions[product] === undefined) {
						if (
							product === 'graph' &&
							isSearchResultGraphEntity(result.node) &&
							result.node.integrationId
						) {
							const thirdPartyProductKey =
								getProductFromProviderId(thirdPartyConfigs, result.node.providerId) ||
								PlatformIntegrationAri.parse(result.node.integrationId).integrationKey;
							if (acc.productsFirstResultPositions[thirdPartyProductKey] === undefined) {
								acc.productsFirstResultPositions[thirdPartyProductKey] = index + 1;
							}

							// this assumes only graph nodes can be 3p
							if (is3pProductKeyForAnalytics(thirdPartyProductKey)) {
								if (!is3PResultPresentInResultSet) {
									is3PResultPresentInResultSet = true;
									is3PInTop10Results = index < 10;
								}

								if (
									isResultSmartLink({
										resultAri: result.node.id,
										thirdPartyConfigs,
										productKey: thirdPartyProductKey,
										connectorType: result.node.connectorType,
									})
								) {
									if (!is3PSmartLinkPresentInResultSet) {
										is3PSmartLinkPresentInResultSet = true;
										is3PSmartLinkInTop10Results = index < 10;
									}
									if (
										acc.smartLinkProductsFirstResultPositions[thirdPartyProductKey] === undefined
									) {
										acc.smartLinkProductsFirstResultPositions[thirdPartyProductKey] = index + 1;
									}
								}
							}
						} else {
							acc.productsFirstResultPositions[product] = index + 1;
						}
					}

					return acc;
				},
				{
					productsFirstResultPositions: {},
					smartLinkProductsFirstResultPositions: {},
				},
			) || {
				productsFirstResultPositions: {},
				smartLinkProductsFirstResultPositions: {},
			};

		return {
			productsFirstResultPositions,
			smartLinkProductsFirstResultPositions,
			is3PInTop10Results,
			is3PResultPresentInResultSet,
			is3PSmartLinkInTop10Results,
			is3PSmartLinkPresentInResultSet,
		};
	}, [results, thirdPartyConfigs]);
}
